import { configureStore, combineReducers, ThunkAction, Action, AnyAction, Middleware, ThunkDispatch } from '@reduxjs/toolkit';
import allReducers, { StoreType } from './slices';
import { asyncLogout, asyncSelectUser, login, selectIsAuthenticated } from './slices/auth';
import { addErrorEvent, apiService } from '../api/apiService';
import { persistReducer, persistStore } from 'redux-persist';
import storage from 'redux-persist/lib/storage'; // defaults to localStorage for web
import { setApiErrorMessage, setErrorMessage } from './slices/global';
import { clear } from './slices/fullProfile';

const persistConfig = {
  key: 'root',
  storage,
  blacklist: ['navigation']
};

const rootReducer = combineReducers(allReducers);

const middleware: Middleware<
  {},
  StoreType,
  ThunkDispatch<any, any, AnyAction>
> = () => {
  return (next) => {
    return (action) => {
      if(action.type === 'auth/login')
        store.dispatch(clear());
      // If our action is redux-persist rehydrate action and our payload contains auth token
      if (action.type === 'persist/REHYDRATE' && action.payload?.auth) {
        // Set the token in the apiService
        apiService.securityToken = action.payload.auth.securityToken;
        apiService.userToken = action.payload.auth.userToken;
      }
      return next(action);
    };
  };
};

// Custom resettable root reducer
const resettableRootReducer = (state: any, action: Action): any => {
  if (action.type === 'store/reset') return rootReducer(undefined, action);
  return rootReducer(state, action);
};

const persistedReducer = persistReducer(persistConfig, resettableRootReducer);

const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) => getDefaultMiddleware({
    serializableCheck: {
      ignoredActions: ['persist/PERSIST']
    }
  }).concat(middleware)
});

window.addEventListener('error', (event) => {
  // event.preventDefault();
  // console.error(event);
  store.dispatch(setErrorMessage(event.message));
  return true;
});

// [GA] quote from Stefan:

// So, for UM we would want some standard handling on every call..
// A. If network issue, display info
// B. If Unauthenticated, redirect to logon page
// C. If 400 & text starts with ERR- display otherwise similar to 500, but generic data error
// D. If 500, then show generic API Failure- perhaps with a technical button which shows actual English error

// [GA]: authentication is handled further down in the file by the token listener

addErrorEvent((err: Error) => {
  let errorIsHandled = false;

  if ((err as any).code === 'ERR_NETWORK') {
    // A: If network issue, display info
    store.dispatch(setApiErrorMessage(err.message));
    errorIsHandled = true;
  }

  if ((err as any)?.response?.data) {
    const data = (err as any).response.data;
    switch ((err as any).response?.status) {
      case 400:
        if (data.description?.indexOf('ERR-') === 0) {
          // C: If 400 & text starts with ERR- display
          store.dispatch(setApiErrorMessage(data.description.substring(4)));
          errorIsHandled = true;
        }
        break;
      case 401: // token expired, but wouldn't show toast
        errorIsHandled = true;
        break;
      case 500:
        // D: If 500, then show generic API Failure
        store.dispatch(setApiErrorMessage(data.description));
        errorIsHandled = true;
        break;
    }
  }
  
  throw err;
});

window.addEventListener('unhandledrejection', (event) => {
  event.preventDefault();
  // console.error(event);
  store.dispatch(setErrorMessage(event.reason?.message));
  return true;
});

export const persistor = persistStore(store);

//[Aqeel]: run initialisers
// (async () => {
//   console.log('Init calls...');
//   if (apiService.userToken && apiService.securityToken)
//     await store.dispatch(asyncSelectUser(apiService.userToken));
//   // await Promise.all(p);
// })();

export type AppDispatch = typeof store.dispatch;
export type RootState = ReturnType<typeof rootReducer>;
export default store;

// export type RootState = ReturnType<typeof store.getState>;
export type AppThunk<ReturnType = void> = ThunkAction<
  ReturnType,
  RootState,
  unknown,
  Action<string>
>;

apiService.securityToken$.subscribe(async securityToken => {
  if (securityToken && securityToken !== store.getState().auth.securityToken) {
    store.dispatch(login({ securityToken }));
  } else {
    if (selectIsAuthenticated(store.getState())) {
      await store.dispatch(asyncLogout());
      // [GA]:  if we where logged out because we got a 403 response
      //        we do not want that the errors is displayed to the user
      //        we silently fail and go appended all dispatches
      window.location.reload();
    }
  }
});

apiService.userToken$.subscribe(async userToken => {
  if (userToken && userToken !== store.getState().auth.userToken) {
    await store.dispatch(asyncSelectUser({ userToken }));
  }
});